import { CevopBrand, ThemeCycleButton } from '@cevop/ui';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SIGNUP_URL, WEB_URL } from '../config';
import { ChevronDown, ChevronRight, CloseIcon, MenuIcon } from './icons';

const NAV = [
  {
    label: 'Product',
    children: [
      { label: 'Business', href: '/business', desc: 'Manage the business behind the counter' },
      { label: 'POS', href: '/pos', desc: 'Fast, controlled point of sale' },
    ],
  },
  { label: 'Solutions', href: '/solutions' },
  { label: 'Pricing', href: '/pricing' },
  {
    label: 'Resources',
    children: [
      { label: 'Help', href: '/help', desc: 'Documentation and guides' },
      { label: 'FAQ', href: '/faq', desc: 'Plans, POS, and billing' },
      { label: 'Journal', href: '/blog', desc: 'How the system works' },
      { label: 'Guides', href: '/guides', desc: 'Written tutorials' },
      { label: 'Security', href: '/security', desc: 'How we protect your data' },
    ],
  },
  { label: 'Company', href: '/about' },
] as const;

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setDropdown(null);
  }, [location]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 right-0 left-0 z-50 transition-all duration-200 ${
          scrolled
            ? 'border-b border-line bg-surface/96 shadow-sm backdrop-blur-md'
            : 'bg-surface/80 backdrop-blur-sm'
        }`}
      >
        <div
          className={`mx-auto flex max-w-[1280px] items-center justify-between px-6 transition-[height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:px-10 ${
            scrolled ? 'h-14' : 'h-16'
          }`}
        >
          <Link to="/" className="text-ink" aria-label="cevop home">
            <CevopBrand wordmarkHeight={18} />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
            {NAV.map((item) =>
              'children' in item ? (
                <div key={item.label} className="relative">
                  <button
                    type="button"
                    aria-expanded={dropdown === item.label}
                    className={`flex items-center gap-1 rounded-lg px-3 py-2 text-[0.875rem] font-medium transition-colors ${
                      dropdown === item.label
                        ? 'bg-brand-light text-brand'
                        : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
                    }`}
                    onClick={() => setDropdown(dropdown === item.label ? null : item.label)}
                    onBlur={(e) => {
                      if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) setDropdown(null);
                    }}
                  >
                    {item.label}
                    <ChevronDown className={`transition-transform duration-150 ${dropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {dropdown === item.label && (
                    <div
                      className="mk-pop absolute top-full left-0 mt-1.5 min-w-[200px] rounded-xl border border-line bg-panel p-1.5 shadow-lg"
                      role="menu"
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="group block rounded-lg px-3 py-2.5 hover:bg-surface-2"
                          role="menuitem"
                        >
                          <div className="text-[0.875rem] font-semibold text-ink group-hover:text-brand">
                            {child.label}
                          </div>
                          <div className="mt-0.5 text-[0.72rem] text-ink-3">{child.desc}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2 text-[0.875rem] font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-light text-brand'
                        : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <ThemeCycleButton />
            <a
              href={WEB_URL}
              className="rounded-lg px-3 py-2 text-[0.875rem] font-medium text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
            >
              Sign in
            </a>
            <a
              href={SIGNUP_URL}
              className="rounded-lg bg-brand px-4 py-2 text-[0.875rem] font-semibold text-brand-on transition-colors duration-150 hover:bg-brand-hover"
            >
              Start free
            </a>
          </div>

          <div className="flex items-center gap-1 md:hidden">
            <ThemeCycleButton />
            <button
              type="button"
              className="rounded-lg p-2 text-ink-2 hover:bg-surface-2"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className="mk-sheet fixed inset-0 z-[100] flex flex-col bg-surface"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex h-16 items-center justify-between border-b border-line px-6">
            <Link to="/" className="text-ink" aria-label="cevop home">
              <CevopBrand wordmarkHeight={18} />
            </Link>
            <div className="flex items-center gap-1">
              <ThemeCycleButton />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="rounded-lg p-2 text-ink-2 hover:bg-surface-2"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <nav className="px-4 pt-4 pb-6">
              <div className="mb-2">
                <div className="mb-1 px-3 text-[0.65rem] font-semibold tracking-widest text-ink-3 uppercase">
                  Product
                </div>
                {[
                  { label: 'Business', href: '/business' },
                  { label: 'POS', href: '/pos' },
                ].map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="flex items-center justify-between rounded-xl px-3 py-3 text-[1rem] font-semibold text-ink hover:bg-surface-2"
                  >
                    {item.label}
                    <ChevronRight className="text-ink-3" />
                  </Link>
                ))}
              </div>
              <div className="my-3 h-px bg-line" />
              {[
                { label: 'Solutions', href: '/solutions' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'Help', href: '/help' },
                { label: 'FAQ', href: '/faq' },
                { label: 'Journal', href: '/blog' },
                { label: 'Guides', href: '/guides' },
                { label: 'Security', href: '/security' },
                { label: 'About', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ].map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center justify-between rounded-xl px-3 py-3 text-[1rem] font-semibold text-ink hover:bg-surface-2"
                >
                  {item.label}
                  <ChevronRight className="text-ink-3" />
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex flex-col gap-3 border-t border-line px-4 pt-2 pb-8">
            <a
              href={WEB_URL}
              className="rounded-xl border border-line py-3.5 text-center text-[1rem] font-semibold text-ink-2"
            >
              Sign in
            </a>
            <a
              href={SIGNUP_URL}
              className="rounded-xl bg-brand py-3.5 text-center text-[1rem] font-semibold text-brand-on"
            >
              Start free
            </a>
          </div>
        </div>
      )}
    </>
  );
}
